import React, {useEffect, useMemo} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {
    exportFsWpIssuesExcel,
    listFsWpIssues,
    updateFsWpIssueStatus,
    type AuditFsWpIssueListItem,
} from '@/services/auditService/auditFsWpIssueService';
import {WP_STATUS, WP_STATUS_LABELS} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/wpStatusUtils';

const ISSUE_STATUS_OPTIONS = [
    {value: 0, label: 'None'},
    {value: 1, label: 'Open'},
    {value: 2, label: 'Resolved'},
];

function isIssueRow(row: AuditFsWpIssueListItem): boolean {
    if (row.wpIssueRemark != null && String(row.wpIssueRemark).trim() !== '') return true;
    if (row.wpStatus === WP_STATUS.HAS_ISSUE) return true;
    if (row.issueStatus != null && row.issueStatus > 0) return true;
    return false;
}

interface IssuesOutstandingPanelProps {
    companyId: number;
    annualId: number;
    /** When true, panel tab is visible — refetch so saves on other tabs appear immediately. */
    active?: boolean;
    onOpenSchedule?: (code: string, statement: 'BS' | 'PL', fsId: number) => void;
}

export default function IssuesOutstandingPanel({
    companyId,
    annualId,
    active = true,
    onOpenSchedule,
}: IssuesOutstandingPanelProps) {
    const queryClient = useQueryClient();
    const [exporting, setExporting] = React.useState(false);
    const [updatingFsId, setUpdatingFsId] = React.useState<number | null>(null);

    const {data, isLoading, isFetching, refetch} = useQuery({
        queryKey: ['audit-fs-wp-issue', companyId, annualId],
        queryFn: () => listFsWpIssues(companyId, annualId),
        enabled: companyId > 0 && annualId > 0,
        staleTime: 0,
    });

    useEffect(() => {
        if (!active || companyId <= 0 || annualId <= 0) return;
        void refetch();
    }, [active, companyId, annualId, refetch]);

    /** MARK AS ISSUE tab: issue rows only — outstanding belongs to EXPORT → OUTSTANDING TO CLIENT. */
    const rows = useMemo(() => (data ?? []).filter(isIssueRow), [data]);

    const handleExport = async () => {
        if (exporting) return;
        try {
            setExporting(true);
            const {blob, filename} = await exportFsWpIssuesExcel(companyId, annualId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } finally {
            setExporting(false);
        }
    };

    const handleStatusChange = async (row: AuditFsWpIssueListItem, issueStatus: number) => {
        if (!row.fsId || updatingFsId != null) return;
        try {
            setUpdatingFsId(row.fsId);
            await updateFsWpIssueStatus({
                companyId,
                annualId,
                fsId: row.fsId,
                issueStatus,
            });
            await queryClient.invalidateQueries({
                queryKey: ['audit-fs-wp-issue', companyId, annualId],
            });
        } finally {
            setUpdatingFsId(null);
        }
    };

    return (
        <Box sx={{p: 2, height: '100%', boxSizing: 'border-box'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={700}>
                    MARK AS ISSUE
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    {isFetching && <CircularProgress size={18} />}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                        disabled={exporting || rows.length === 0}
                        onClick={() => void handleExport()}
                    >
                        Export
                    </Button>
                </Stack>
            </Stack>

            {isLoading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress />
                </Box>
            ) : rows.length === 0 ? (
                <Paper variant="outlined" sx={{p: 3}}>
                    <Typography color="text.secondary">No marked issues for this engagement.</Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{maxHeight: 'calc(100% - 56px)'}}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>WP Code</TableCell>
                                <TableCell>Schedule</TableCell>
                                <TableCell>Statement</TableCell>
                                <TableCell>WP Status</TableCell>
                                <TableCell>Issue Status</TableCell>
                                <TableCell>Issue Remark</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => {
                                const code = row.code || row.scheduleCode || '';
                                const statement = (row.statement || 'BS') as 'BS' | 'PL';
                                const displayName = row.name || code || '-';
                                const wpCode = row.wpCode || '-';
                                return (
                                    <TableRow key={`${row.fsId}-${code}`} hover>
                                        <TableCell>{wpCode}</TableCell>
                                        <TableCell>
                                            {onOpenSchedule && code ? (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() => onOpenSchedule(code, statement, row.fsId)}
                                                >
                                                    {displayName}
                                                </Button>
                                            ) : (
                                                displayName
                                            )}
                                        </TableCell>
                                        <TableCell>{row.statement}</TableCell>
                                        <TableCell>
                                            {row.wpStatus != null ? WP_STATUS_LABELS[row.wpStatus] ?? row.wpStatus : '-'}
                                        </TableCell>
                                        <TableCell sx={{minWidth: 140}}>
                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    value={row.issueStatus ?? 0}
                                                    disabled={updatingFsId === row.fsId}
                                                    onChange={(e) => void handleStatusChange(row, Number(e.target.value))}
                                                >
                                                    {ISSUE_STATUS_OPTIONS.map((opt) => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell sx={{whiteSpace: 'pre-wrap', maxWidth: 220}}>
                                            {row.wpIssueRemark || '-'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
